import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  {
    title: "Movement",
    links: [
      { label: "About", href: "#movement" },
      { label: "The Book", href: "#book" },
      { label: "Curriculum", href: "#curriculum" },
      { label: "Speaking", href: "#speaking" }
    ]
  },
  {
    title: "Engage",
    links: [
      { label: "Sponsor A Youth", href: "#sponsor" },
      { label: "Podcast & Media", href: "#media" },
      { label: "Speaking", href: "#speaking" },
      { label: "Book", href: "#book" }
    ]
  },
  {
    title: "Inquiries",
    links: [
      { label: "Schools & Districts", href: "#contact" },
      { label: "Speaking Bookings", href: "#speaking" },
      { label: "Media Requests", href: "#contact" },
      { label: "Sponsorship", href: "#contact" }
    ]
  }
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Contact form
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactSending, setContactSending] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: "footer" });
      toast.success("You're in. Welcome to the movement.");
      setEmail("");
    } catch (err) {
      toast.error("Hmm — that didn't go through. Try again.");
    } finally { setSubmitting(false); }
  };

  const onContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Name, email, and message required.");
      return;
    }
    setContactSending(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message received. We'll be in touch.");
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send. Email info@mrgreenbay.com directly.");
    } finally { setContactSending(false); }
  };

  return (
    <footer id="footer" data-testid="site-footer" className="relative overflow-hidden bg-ink-900 pt-24">
      <div className="divider-gold" />

      {/* Contact + newsletter */}
      <div id="contact" className="mx-auto max-w-7xl px-6 py-20 md:px-12">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" />
              Contact
            </div>
            <h3 className="font-display text-4xl font-bold leading-tight text-white md:text-5xl">
              Start the <span className="text-gradient-gold">conversation.</span>
            </h3>
            <p className="mt-5 max-w-md text-zinc-300">
              Whether it's a school, a stage, a sponsorship, or a story — we read every message.
            </p>

            <div className="mt-8 space-y-3 text-sm text-zinc-300">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-gold" /> info@mrgreenbay.com
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-globe text-gold" /> MrGreenBay.com
              </div>
              <a
                href="https://www.instagram.com/mr_greenbay/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-gold transition-colors"
              >
                <i className="fa-brands fa-instagram text-gold" /> @mr_greenbay
              </a>
              <a
                href="https://www.tiktok.com/@mrgreenbay920"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-gold transition-colors"
              >
                <i className="fa-brands fa-tiktok text-gold" /> @mrgreenbay920
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {[
                { i: "fa-facebook-f", label: "facebook", url: "https://www.facebook.com/Mr.GreenBay/" },
                { i: "fa-youtube", label: "youtube", url: "https://www.youtube.com/@MrGreenBay" },
                { i: "fa-instagram", label: "instagram-damien", url: "https://www.instagram.com/damien_doran/", title: "@damien_doran" },
                { i: "fa-instagram", label: "instagram-mrgreenbay", url: "https://www.instagram.com/mr_greenbay/", title: "@mr_greenbay" },
                { i: "fa-tiktok", label: "tiktok", url: "https://www.tiktok.com/@mrgreenbay920" }
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${s.label}`}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold"
                  aria-label={s.title || s.label}
                  title={s.title || s.label}
                >
                  <i className={`fa-brands ${s.i}`} />
                </a>
              ))}
            </div>

            <form onSubmit={onSubscribe} className="mt-10" data-testid="newsletter-form">
              <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-zinc-500">
                Newsletter
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email"
                  placeholder="you@email.com"
                  className="flex-1 rounded-full border border-white/10 bg-black/40 px-5 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit"
                  className="btn-gold rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-wider disabled:opacity-60"
                >
                  {submitting ? "Joining…" : "Join The Movement"}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-zinc-500">
                Drops, dates, and direct messages from Mr. Green Bay. No spam.
              </p>
            </form>
          </div>

          {/* Contact form */}
          <form
            onSubmit={onContact}
            data-testid="contact-form"
            className="glass-gold lg:col-span-7 rounded-3xl p-7 md:p-9"
          >
            <div className="mb-2 text-xs uppercase tracking-[0.3em] text-gold">Send A Message</div>
            <div className="font-display text-2xl text-white md:text-3xl">
              Schools · Sponsors · Media · Curriculum
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <FInput label="Your Name" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} testid="contact-name" required />
              <FInput label="Email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="contact-email" required />
            </div>
            <div className="mt-4">
              <FInput label="Subject" value={contact.subject} onChange={(v) => setContact({ ...contact, subject: v })} testid="contact-subject" />
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Message *</label>
              <textarea
                data-testid="contact-message"
                rows={5}
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
                placeholder="Tell us how we can serve your community."
                required
              />
            </div>
            <button
              type="submit"
              disabled={contactSending}
              data-testid="contact-submit"
              className="btn-gold mt-6 inline-flex rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider disabled:opacity-60"
            >
              {contactSending ? (
                <><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Sending</>
              ) : (
                <><i className="fa-solid fa-paper-plane mr-2" /> Send Message</>
              )}
            </button>
          </form>
        </div>

        {/* Link columns */}
        <div className="mt-20 grid gap-10 border-t border-white/5 pt-12 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_crash-proof-movement/artifacts/j30fas3r_40BB10F5-2D42-4AAE-9504-A5FC7C4F46EA.PNG"
                alt="Mr. Green Bay"
                className="h-16 w-16 rounded-md object-contain"
              />
              <div>
                <div className="font-display text-lg font-bold text-white">CA$H OUT</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80">Never Crash Out</div>
              </div>
            </div>
            <p className="mt-5 max-w-xs text-sm text-zinc-400">
              Leadership. Loyalty. Legacy. A movement helping youth choose purpose over destruction.
            </p>
          </div>
          {COLS.map((c) => (
            <div key={c.title}>
              <div className="mb-4 text-xs uppercase tracking-[0.25em] text-gold">{c.title}</div>
              <ul className="space-y-2">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm text-zinc-300 transition-colors hover:text-gold"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-8 text-xs text-zinc-500 md:flex-row">
          <div>© {new Date().getFullYear()} CA$H OUT NEVER CRASH OUT · Damien Doran. All rights reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-gold">Privacy</a>
            <a href="#" className="hover:text-gold">Terms</a>
            <a href="#" className="hover:text-gold">Press Kit</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FInput({ label, value, onChange, testid, type = "text", required = false }) {
  return (
    <div>
      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
      />
    </div>
  );
}
